/**
 * Comissoes SIMULADAS (RNF19) -- NAO E PRODUCAO.
 *
 * Faz o papel do servidor: gera o extrato, fecha os agregados e responde as
 * mesmas perguntas que `/api/admin/commissions` e `/conversions` vao
 * responder (filtros, facetas, ordenacao, pagina, exportacao).
 *
 * Como o extrato nasce:
 *  1. Uma conversao por venda recuperada, dia a dia, para cada empresa ATIVA
 *     do catalogo, a partir da data de cadastro dela (empresa nao converte
 *     antes de existir; em implantacao e pausada nao recebem webhook). O
 *     volume vem de um hash estavel do id e do DIA ABSOLUTO -- nunca da
 *     posicao do dia na janela --, entao 24/09 tem as mesmas vendas em "7
 *     dias", "30 dias" e "12 meses". Nada de Math.random, e nada depois de
 *     AGORA_SIMULADO.
 *  2. O EXTRATO E A FONTE. Resumo, serie, empresas, mes corrente e facetas
 *     sao somas das mesmas linhas: cartoes, grafico e tabela batem entre si.
 *  3. Comissao e `round(valorPago * feeBps / 10000)` POR CONVERSAO, com o
 *     percentual do CATALOGO (o historico). Se a administracao alterou o
 *     percentual na previa, a tela le o vigente em `useCompany()`; o que ja
 *     foi apurado fica congelado, como no backend (ADR-001).
 *  4. Contato creditado e sempre um toque do motor: metade ligacao da IA,
 *     40% WhatsApp, 10% e-mail. Em empresas com vendedora cadastrada
 *     (papel `comercial` no catalogo), 3 em cada 5 conversoes creditadas a um
 *     WhatsApp foram FECHADAS pela vendedora (o lead respondeu ao toque); uma
 *     em cada tres dessas entrou como venda assistida (registro manual, sem
 *     webhook). O credito continua do toque (E5).
 *  5. Uma em cada 19 conversoes recebe um estorno de 1 a 9 dias depois do
 *     pagamento (se ja tiver acontecido): metade reembolso total, um sexto
 *     reembolso parcial (30% do valor), um terco chargeback. A reversao segue
 *     a politica da empresa (catalogo; padrao `reverte_chargeback`) e fica
 *     congelada na linha.
 *  6. Status pelo mes da fatura, relativo a AGORA_SIMULADO: mes corrente = a
 *     faturar; mes anterior = paga (empresas de hash par) ou faturada
 *     (impar); mais antigos = paga.
 */

import { AGORA_SIMULADO } from '../../lib/api';
import { EMPRESAS, empresaPorId, equipeDaEmpresa, ofertaCiclica } from '../../lib/mock/catalogo';
import type { CompanySummary, Page, PeriodRange, TeamMember } from '../../types';
import { aplicarBps } from './comparar';
import { normalizar, opcoesDaLinha, passaNosFiltros, POLITICA_PADRAO, reverteComissao, type DimensaoDoFiltro } from './regras';
import type {
  AmountBand,
  ChannelFilter,
  ClosedByFilter,
  CommissionSeriesPoint,
  CommissionsOverview,
  CommissionSummary,
  CompanyCommissionSummary,
  ComercialTotals,
  ContactChannel,
  ConversionFacets,
  ConversionQuery,
  ConversionRow,
  ConversionsExport,
  ConversionsResponse,
  ConversionStatus,
  InvoiceStatus,
  MonthToDate,
  PeriodTotals,
  RefundBucket,
  RefundEvent,
  RefundFilter,
  RefundKind,
  SellerTotal,
  SortColumn,
  StatusBucket,
  StatusFilter,
} from './types';

const DIA_MS = 86_400_000;
const HORA_MS = 3_600_000;
const MINUTO_MS = 60_000;
const DIAS_MAXIMO = 366;
const AGORA_MS = AGORA_SIMULADO.getTime();
/** Teto da exportacao no navegador; acima disso o servidor gera o arquivo. */
export const LIMITE_DA_EXPORTACAO = 5_000;

/** Hash pequeno e estavel do id: mesmo id, mesmo numero, em qualquer maquina. */
export function hashSimulado(texto: string): number {
  let h = 7;
  for (let i = 0; i < texto.length; i += 1) {
    h = (h * 31 + texto.charCodeAt(i)) % 10_007;
  }
  return h;
}

function isoData(ms: number): string {
  return new Date(ms).toISOString();
}

function yyyymm(ms: number): string {
  return isoData(ms).slice(0, 7);
}

function meiaNoiteUtc(ms: number): number {
  return Math.floor(ms / DIA_MS) * DIA_MS;
}

// -----------------------------------------------------------------------------
// Janela
// -----------------------------------------------------------------------------

interface Janela {
  readonly inicioMs: number;
  /** Exclusivo. */
  readonly fimMs: number;
  readonly dias: number;
}

/** Primeiro dia com empresa cadastrada: e onde "desde o inicio" comeca. */
function inicioDaHistoria(): number {
  const datas = EMPRESAS.map((e) => new Date(e.createdAt).getTime()).filter((ms) => Number.isFinite(ms));
  return meiaNoiteUtc(datas.length === 0 ? AGORA_MS - 30 * DIA_MS : Math.min(...datas));
}

function janelaDe(range: PeriodRange): Janela {
  // Sem `to`, a proxima meia-noite UTC depois de agora, para "hoje" entrar.
  const fimMs = range.to !== null ? new Date(range.to).getTime() : meiaNoiteUtc(AGORA_MS) + DIA_MS;
  const inicioMs = range.from !== null ? new Date(range.from).getTime() : inicioDaHistoria();
  const dias = Math.max(1, Math.min(DIAS_MAXIMO, Math.ceil((fimMs - inicioMs) / DIA_MS)));
  return { inicioMs, fimMs, dias };
}

/** Janela anterior de mesmo tamanho; "desde o inicio" nao tem anterior. */
function janelaAnterior(range: PeriodRange, atual: Janela): Janela | null {
  if (range.from === null || range.to === null) {
    return null;
  }
  const duracao = atual.fimMs - atual.inicioMs;
  return { inicioMs: atual.inicioMs - duracao, fimMs: atual.inicioMs, dias: atual.dias };
}

// -----------------------------------------------------------------------------
// Geracao de linhas
// -----------------------------------------------------------------------------

/** Nomes FICTICIOS, ciclados por semente. Um em cada 17 sai nulo para a tela exercitar o marcador de ausencia. */
const NOMES_DE_LEADS: readonly string[] = [
  'Mariana Ferraz',
  'João Batista Lemos',
  'Aline Souza Prado',
  'Ricardo Nunes',
  'Beatriz Camargo',
  'Fernanda Lopes',
  'Carlos Eduardo Pires',
  'Marcos Vinícius Rocha',
  'Juliana Teixeira',
  'Rafael Monteiro',
  'Larissa Andrade',
  'Gustavo Henrique Melo',
  'Priscila Nogueira',
  'André Luiz Barros',
  'Camila Duarte',
  'Felipe Cardoso',
  'Tatiane Ribeiro',
  'Bruno Sales',
  'Vanessa Queiroz',
  'Leonardo Farias',
];

/** Dez posicoes = distribuicao dos toques creditados (50 / 40 / 10). */
const CANAIS_POR_DEZ: readonly ContactChannel[] = [
  'ligacao_ia',
  'whatsapp',
  'ligacao_ia',
  'whatsapp',
  'ligacao_ia',
  'email',
  'ligacao_ia',
  'whatsapp',
  'ligacao_ia',
  'whatsapp',
];

/** Passos da regua em que cada canal toca o lead (minutos desde o abandono). */
const PASSOS_DA_REGUA: Readonly<Record<ContactChannel, readonly number[]>> = {
  ligacao_ia: [30, 30, 45],
  whatsapp: [60, 1_440, 2_880],
  email: [60, 1_440, 2_880],
};

/** Minutos entre o toque e o pagamento. Todos abaixo de 7 dias (10.080 min), como manda o ADR-001. */
const ATRASOS_ATE_PAGAR: readonly number[] = [12, 47, 130, 380, 1_500, 4_300, 8_900];

const A_CADA_QUANTAS_ESTORNA = 19;
const A_CADA_QUANTOS_SEM_NOME = 17;

interface Calendario {
  readonly mesAtual: string;
  readonly mesAnterior: string;
}

function calendarioDeAgora(): Calendario {
  const atual = Date.UTC(AGORA_SIMULADO.getUTCFullYear(), AGORA_SIMULADO.getUTCMonth(), 1);
  const anterior = Date.UTC(AGORA_SIMULADO.getUTCFullYear(), AGORA_SIMULADO.getUTCMonth() - 1, 1);
  return { mesAtual: yyyymm(atual), mesAnterior: yyyymm(anterior) };
}

/** Chaves YYYY-MM comparam como texto: mes atual ou posterior esta em aberto; o anterior alterna por empresa. */
function statusPorMes(invoiceMonth: string, calendario: Calendario, hash: number): ConversionStatus {
  if (invoiceMonth >= calendario.mesAtual) {
    return 'a_faturar';
  }
  if (invoiceMonth === calendario.mesAnterior) {
    return hash % 2 === 0 ? 'paga' : 'faturada';
  }
  return 'paga';
}

function estornoSimulado(empresa: CompanySummary, semente: number, paidAtMs: number, pagoCents: number): RefundEvent | null {
  if (semente % A_CADA_QUANTAS_ESTORNA !== 0) {
    return null;
  }
  const atMs = paidAtMs + (1 + (semente % 9)) * DIA_MS + (semente % 7) * HORA_MS;
  if (atMs > AGORA_MS) {
    return null;
  }
  const posicao = Math.floor(semente / A_CADA_QUANTAS_ESTORNA) % 6;
  const kind: RefundKind = posicao < 3 ? 'reembolso' : posicao === 3 ? 'reembolso_parcial' : 'chargeback';
  const politica = empresa.refundPolicy ?? POLITICA_PADRAO;
  return {
    kind,
    at: isoData(atMs),
    amountCents: kind === 'reembolso_parcial' ? aplicarBps(pagoCents, 3_000) : pagoCents,
    policyApplied: politica,
    reversed: reverteComissao(politica, kind),
  };
}

function conversaoSimulada(
  empresa: CompanySummary,
  vendedoras: readonly TeamMember[],
  hash: number,
  diaAbs: number,
  j: number,
  calendario: Calendario,
): ConversionRow {
  const semente = hash + diaAbs * 31 + j * 7;
  const oferta = ofertaCiclica(empresa.id, semente);
  const hora = 8 + (semente % 12);
  const minuto = (semente * 13) % 60;
  const paidAtMs = diaAbs * DIA_MS + hora * HORA_MS + minuto * MINUTO_MS;
  const dataCurta = isoData(paidAtMs).slice(0, 10);

  const canal = CANAIS_POR_DEZ[semente % CANAIS_POR_DEZ.length] ?? 'ligacao_ia';
  const passos = PASSOS_DA_REGUA[canal];
  const offsetMinutes = passos[Math.floor(semente / 3) % passos.length] ?? 30;
  const atraso = ATRASOS_ATE_PAGAR[semente % ATRASOS_ATE_PAGAR.length] ?? 60;

  // E5: o lead respondeu ao WhatsApp da regua (numero do comercial) e a vendedora fechou.
  const vendedora = canal === 'whatsapp' && vendedoras.length > 0 && Math.floor(semente / 5) % 5 < 3 ? vendedoras[Math.floor(semente / 7) % vendedoras.length] : undefined;
  const assistida = vendedora !== undefined && Math.floor(semente / 11) % 3 === 0;

  const pagoCents = oferta.priceCents;
  const refund = estornoSimulado(empresa, semente, paidAtMs, pagoCents);
  const invoiceMonth = yyyymm(paidAtMs);
  const nome = semente % A_CADA_QUANTOS_SEM_NOME === 0 ? null : (NOMES_DE_LEADS[semente % NOMES_DE_LEADS.length] ?? null);

  return {
    id: `SIMULADO-conversao-${empresa.slug}-${dataCurta}-${j + 1}`,
    companyId: empresa.id,
    companyName: empresa.name,
    leadId: `SIMULADO-lead-${empresa.slug}-${dataCurta}-${j + 1}`,
    leadName: nome,
    offerId: oferta.id,
    offerName: oferta.name,
    source: assistida ? 'manual' : oferta.provider,
    paidAt: isoData(paidAtMs),
    paidAmountCents: pagoCents,
    feeBpsApplied: empresa.feeBps,
    commissionCents: aplicarBps(pagoCents, empresa.feeBps),
    creditedContact: { channel: canal, at: isoData(paidAtMs - atraso * MINUTO_MS), offsetMinutes },
    closedBy:
      vendedora === undefined
        ? null
        : {
            sellerId: vendedora.id,
            sellerName: vendedora.name,
            via: assistida ? 'venda_assistida' : 'webhook',
            orderRef: assistida ? `SIMULADO-pix-${empresa.slug}-${dataCurta}-${j + 1}` : null,
            registeredAt: assistida ? isoData(Math.min(AGORA_MS, paidAtMs + (15 + (semente % 150)) * MINUTO_MS)) : null,
          },
    refund,
    status: refund !== null && refund.reversed ? 'revertida' : statusPorMes(invoiceMonth, calendario, hash),
    invoiceMonth,
  };
}

function linhasDaEmpresa(empresa: CompanySummary, inicioMs: number, fimMs: number, calendario: Calendario): readonly ConversionRow[] {
  if (empresa.status !== 'ativa') {
    return [];
  }
  const cadastroMs = new Date(empresa.createdAt).getTime();
  const desdeMs = Math.max(inicioMs, Number.isFinite(cadastroMs) ? cadastroMs : inicioMs);
  const ateMs = Math.min(fimMs, AGORA_MS + 1);
  if (ateMs <= desdeMs) {
    return [];
  }
  const vendedoras = equipeDaEmpresa(empresa.id).filter((m) => m.role === 'comercial');
  const hash = hashSimulado(empresa.id);
  const porDiaUtil = 2 + (hash % 3);
  const linhas: ConversionRow[] = [];
  for (let d = Math.floor(desdeMs / DIA_MS); d <= Math.floor((ateMs - 1) / DIA_MS); d += 1) {
    const diaDaSemana = new Date(d * DIA_MS).getUTCDay();
    const fimDeSemana = diaDaSemana === 0 || diaDaSemana === 6;
    // Onda deterministica: fim de semana a 50%, dias uteis entre 80% e 120%.
    const ondaPct = fimDeSemana ? 50 : 80 + ((d * 7 + hash) % 9) * 5;
    const quantidade = Math.round((porDiaUtil * ondaPct) / 100);
    for (let j = 0; j < quantidade; j += 1) {
      const linha = conversaoSimulada(empresa, vendedoras, hash, d, j, calendario);
      const pagoMs = new Date(linha.paidAt).getTime();
      if (pagoMs >= desdeMs && pagoMs < ateMs) {
        linhas.push(linha);
      }
    }
  }
  return linhas;
}

/** Do mais recente ao mais antigo; o id desempata para a ordem nao depender da insercao. */
function ordemPadrao(a: ConversionRow, b: ConversionRow): number {
  if (a.paidAt !== b.paidAt) {
    return a.paidAt < b.paidAt ? 1 : -1;
  }
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

const CACHE = new Map<string, readonly ConversionRow[]>();
const TAMANHO_DO_CACHE = 8;

/** Todas as linhas de todas as empresas no intervalo, ja na ordem padrao. Memorizado: a tela pede a mesma janela varias vezes. */
function linhasDoIntervalo(inicioMs: number, fimMs: number): readonly ConversionRow[] {
  const chave = `${inicioMs}|${fimMs}`;
  const emCache = CACHE.get(chave);
  if (emCache !== undefined) {
    return emCache;
  }
  const calendario = calendarioDeAgora();
  const linhas = EMPRESAS.flatMap((empresa) => linhasDaEmpresa(empresa, inicioMs, fimMs, calendario)).sort(ordemPadrao);
  if (CACHE.size >= TAMANHO_DO_CACHE) {
    const maisAntiga = CACHE.keys().next();
    if (maisAntiga.done !== true) {
      CACHE.delete(maisAntiga.value);
    }
  }
  CACHE.set(chave, linhas);
  return linhas;
}

function doRecorte(linhas: readonly ConversionRow[], companyId: string | null): readonly ConversionRow[] {
  return companyId === null ? linhas : linhas.filter((l) => l.companyId === companyId);
}

// -----------------------------------------------------------------------------
// Consolidacao
// -----------------------------------------------------------------------------

const BALDE_VAZIO: StatusBucket = { count: 0, commissionCents: 0, recoveredAmountCents: 0 };

function somarNoBalde(balde: StatusBucket, linha: ConversionRow): StatusBucket {
  return {
    count: balde.count + 1,
    commissionCents: balde.commissionCents + linha.commissionCents,
    recoveredAmountCents: balde.recoveredAmountCents + linha.paidAmountCents,
  };
}

function ativa(linha: ConversionRow): boolean {
  return linha.status !== 'revertida';
}

function fecharTotais(linhas: readonly ConversionRow[]): PeriodTotals {
  let commissionCents = 0;
  let recoveredAmountCents = 0;
  let conversionCount = 0;
  let reversedCount = 0;
  let reversedCommissionCents = 0;
  let refundCount = 0;
  let refundedAmountCents = 0;
  let comercialCount = 0;
  let comercialAmountCents = 0;
  let comercialCommissionCents = 0;
  for (const l of linhas) {
    if (l.refund !== null) {
      refundCount += 1;
      refundedAmountCents += l.refund.amountCents;
    }
    if (!ativa(l)) {
      reversedCount += 1;
      reversedCommissionCents += l.commissionCents;
      continue;
    }
    conversionCount += 1;
    commissionCents += l.commissionCents;
    recoveredAmountCents += l.paidAmountCents;
    if (l.closedBy !== null) {
      comercialCount += 1;
      comercialAmountCents += l.paidAmountCents;
      comercialCommissionCents += l.commissionCents;
    }
  }
  return {
    commissionCents,
    recoveredAmountCents,
    conversionCount,
    reversedCount,
    reversedCommissionCents,
    refundCount,
    refundedAmountCents,
    comercialCount,
    comercialAmountCents,
    comercialCommissionCents,
  };
}

const ESTORNO_VAZIO: RefundBucket = {
  count: 0,
  refundedAmountCents: 0,
  reversedCount: 0,
  reversedCommissionCents: 0,
  keptCommissionCents: 0,
  commissionAtStakeCents: 0,
};

function fecharComercial(linhas: readonly ConversionRow[]): ComercialTotals {
  let viaWebhook = BALDE_VAZIO;
  let assisted = BALDE_VAZIO;
  const porVendedora = new Map<string, SellerTotal>();
  for (const l of linhas) {
    if (l.closedBy === null || !ativa(l)) {
      continue;
    }
    const assistida = l.closedBy.via === 'venda_assistida';
    if (assistida) {
      assisted = somarNoBalde(assisted, l);
    } else {
      viaWebhook = somarNoBalde(viaWebhook, l);
    }
    const atual: SellerTotal = porVendedora.get(l.closedBy.sellerId) ?? {
      sellerId: l.closedBy.sellerId,
      sellerName: l.closedBy.sellerName,
      companyId: l.companyId,
      companyName: l.companyName,
      count: 0,
      amountCents: 0,
      commissionCents: 0,
      assistedCount: 0,
    };
    porVendedora.set(l.closedBy.sellerId, {
      ...atual,
      count: atual.count + 1,
      amountCents: atual.amountCents + l.paidAmountCents,
      commissionCents: atual.commissionCents + l.commissionCents,
      assistedCount: atual.assistedCount + (assistida ? 1 : 0),
    });
  }
  return {
    count: viaWebhook.count + assisted.count,
    amountCents: viaWebhook.recoveredAmountCents + assisted.recoveredAmountCents,
    commissionCents: viaWebhook.commissionCents + assisted.commissionCents,
    viaWebhook,
    assisted,
    sellers: [...porVendedora.values()].sort((a, b) => b.commissionCents - a.commissionCents || a.sellerName.localeCompare(b.sellerName, 'pt-BR')),
  };
}

function fecharResumo(linhas: readonly ConversionRow[]): CommissionSummary {
  const byStatus: Record<ConversionStatus, StatusBucket> = { a_faturar: BALDE_VAZIO, faturada: BALDE_VAZIO, paga: BALDE_VAZIO, revertida: BALDE_VAZIO };
  const refunds: Record<RefundKind, RefundBucket> = { reembolso: ESTORNO_VAZIO, reembolso_parcial: ESTORNO_VAZIO, chargeback: ESTORNO_VAZIO };
  let webhook = BALDE_VAZIO;
  let manual = BALDE_VAZIO;
  for (const l of linhas) {
    byStatus[l.status] = somarNoBalde(byStatus[l.status], l);
    if (l.refund !== null) {
      const b = refunds[l.refund.kind];
      refunds[l.refund.kind] = {
        count: b.count + 1,
        refundedAmountCents: b.refundedAmountCents + l.refund.amountCents,
        reversedCount: b.reversedCount + (l.refund.reversed ? 1 : 0),
        reversedCommissionCents: b.reversedCommissionCents + (l.refund.reversed ? l.commissionCents : 0),
        keptCommissionCents: b.keptCommissionCents + (l.refund.reversed ? 0 : l.commissionCents),
        commissionAtStakeCents: b.commissionAtStakeCents + l.commissionCents,
      };
    }
    if (ativa(l)) {
      if (l.source === 'manual') {
        manual = somarNoBalde(manual, l);
      } else {
        webhook = somarNoBalde(webhook, l);
      }
    }
  }
  return { ...fecharTotais(linhas), byStatus, refunds, comercial: fecharComercial(linhas), activeBySource: { webhook, manual } };
}

/** Serie DENSA (um ponto por dia da janela, zeros incluidos), so com conversoes ativas. */
function fecharSerie(linhas: readonly ConversionRow[], janela: Janela): readonly CommissionSeriesPoint[] {
  const recuperado: number[] = Array.from({ length: janela.dias }, () => 0);
  const comissao: number[] = Array.from({ length: janela.dias }, () => 0);
  for (const linha of linhas) {
    if (!ativa(linha)) {
      continue;
    }
    const indice = Math.floor((new Date(linha.paidAt).getTime() - janela.inicioMs) / DIA_MS);
    if (indice < 0 || indice >= janela.dias) {
      continue;
    }
    recuperado[indice] = (recuperado[indice] ?? 0) + linha.paidAmountCents;
    comissao[indice] = (comissao[indice] ?? 0) + linha.commissionCents;
  }
  return Array.from({ length: janela.dias }, (_, i) => ({
    day: isoData(janela.inicioMs + i * DIA_MS).slice(0, 10),
    recoveredAmountCents: recuperado[i] ?? 0,
    commissionCents: comissao[i] ?? 0,
  }));
}

function faturaDoMes(linhas: readonly ConversionRow[], calendario: Calendario): InvoiceStatus {
  return linhas.some((l) => ativa(l) && l.invoiceMonth === calendario.mesAtual) ? 'a_faturar' : 'sem_movimento';
}

function fecharEmpresa(
  empresa: CompanySummary,
  linhas: readonly ConversionRow[],
  anteriores: readonly ConversionRow[] | null,
  calendario: Calendario,
): CompanyCommissionSummary {
  const totais = fecharTotais(linhas);
  return {
    companyId: empresa.id,
    companyName: empresa.name,
    feeBps: empresa.feeBps,
    feeBpsInPeriod: [...new Set(linhas.map((l) => l.feeBpsApplied))].sort((a, b) => a - b),
    billingModel: empresa.billingModel,
    refundPolicy: empresa.refundPolicy ?? POLITICA_PADRAO,
    conversionCount: totais.conversionCount,
    recoveredAmountCents: totais.recoveredAmountCents,
    commissionCents: totais.commissionCents,
    previousCommissionCents: anteriores === null ? null : fecharTotais(anteriores).commissionCents,
    pendingCommissionCents: linhas.filter((l) => l.status === 'a_faturar').reduce((acc, l) => acc + l.commissionCents, 0),
    reversedCount: totais.reversedCount,
    reversedCommissionCents: totais.reversedCommissionCents,
    refundCount: totais.refundCount,
    refundedAmountCents: totais.refundedAmountCents,
    comercialCount: totais.comercialCount,
    comercialAmountCents: totais.comercialAmountCents,
    invoice: { month: calendario.mesAtual, status: faturaDoMes(linhas, calendario) },
  };
}

function empresasNoRecorte(companyId: string | null): readonly CompanySummary[] {
  if (companyId === null) {
    return EMPRESAS;
  }
  const uma = empresaPorId(companyId);
  return uma === null ? [] : [uma];
}

function mesAteAgora(companyId: string | null): MonthToDate {
  const ano = AGORA_SIMULADO.getUTCFullYear();
  const mes = AGORA_SIMULADO.getUTCMonth();
  const inicioDoMes = Date.UTC(ano, mes, 1);
  const inicioDoAnterior = Date.UTC(ano, mes - 1, 1);
  const linhas = doRecorte(linhasDoIntervalo(inicioDoMes, meiaNoiteUtc(AGORA_MS) + DIA_MS), companyId);
  const anteriores = doRecorte(linhasDoIntervalo(inicioDoAnterior, inicioDoMes), companyId);
  const byCompany = empresasNoRecorte(companyId).map((empresa) => {
    const t = fecharTotais(linhas.filter((l) => l.companyId === empresa.id));
    return { companyId: empresa.id, recoveredAmountCents: t.recoveredAmountCents, commissionCents: t.commissionCents, conversionCount: t.conversionCount };
  });
  return {
    monthKey: yyyymm(inicioDoMes),
    daysElapsed: AGORA_SIMULADO.getUTCDate(),
    daysInMonth: new Date(Date.UTC(ano, mes + 1, 0)).getUTCDate(),
    byCompany,
    previousMonthCommissionCents: fecharTotais(anteriores).commissionCents,
  };
}

// -----------------------------------------------------------------------------
// Respostas
// -----------------------------------------------------------------------------

export function simularVisao(range: PeriodRange, companyId: string | null): CommissionsOverview {
  const janela = janelaDe(range);
  const anterior = janelaAnterior(range, janela);
  const calendario = calendarioDeAgora();

  const todas = linhasDoIntervalo(janela.inicioMs, janela.fimMs);
  const linhas = doRecorte(todas, companyId);
  const anteriores = anterior === null ? null : doRecorte(linhasDoIntervalo(anterior.inicioMs, anterior.fimMs), companyId);

  const companies = empresasNoRecorte(companyId)
    .map((empresa) =>
      fecharEmpresa(
        empresa,
        linhas.filter((l) => l.companyId === empresa.id),
        anteriores === null ? null : anteriores.filter((l) => l.companyId === empresa.id),
        calendario,
      ),
    )
    .sort((a, b) => b.commissionCents - a.commissionCents || a.companyName.localeCompare(b.companyName, 'pt-BR'));

  return {
    summary: fecharResumo(linhas),
    previous: anteriores === null ? null : fecharTotais(anteriores),
    // Fins INCLUSIVOS (ultimo instante): a janela interna e semiaberta, e exibir o fim exclusivo
    // faria o periodo anterior "terminar" no primeiro dia do atual.
    previousPeriod: anterior === null ? null : { start: isoData(anterior.inicioMs), end: isoData(anterior.fimMs - 1) },
    series: fecharSerie(linhas, janela),
    companies,
    companyFacets: EMPRESAS.map((e) => ({ companyId: e.id, companyName: e.name, conversionCount: todas.filter((l) => l.companyId === e.id).length })),
    month: mesAteAgora(companyId),
    periodStart: isoData(janela.inicioMs),
    periodEnd: isoData(Math.min(janela.fimMs - 1, AGORA_MS)),
  };
}

const PESO_DO_STATUS: Readonly<Record<ConversionStatus, number>> = { a_faturar: 0, faturada: 1, paga: 2, revertida: 3 };
const PESO_DO_CANAL: Readonly<Record<ContactChannel, number>> = { ligacao_ia: 0, whatsapp: 1, email: 2 };

function compararPor(a: ConversionRow, b: ConversionRow, coluna: SortColumn): number {
  switch (coluna) {
    case 'paidAt':
      return a.paidAt < b.paidAt ? -1 : a.paidAt > b.paidAt ? 1 : 0;
    case 'company':
      return a.companyName.localeCompare(b.companyName, 'pt-BR');
    case 'amount':
      return a.paidAmountCents - b.paidAmountCents;
    case 'fee':
      return a.feeBpsApplied - b.feeBpsApplied;
    case 'commission':
      return a.commissionCents - b.commissionCents;
    case 'contact':
      return PESO_DO_CANAL[a.creditedContact.channel] - PESO_DO_CANAL[b.creditedContact.channel] || a.creditedContact.offsetMinutes - b.creditedContact.offsetMinutes;
    case 'closedBy':
      // "Direto no checkout" (sem vendedora) vem antes de qualquer nome na ordem crescente.
      return (a.closedBy?.sellerName ?? '').localeCompare(b.closedBy?.sellerName ?? '', 'pt-BR');
    case 'status':
      return PESO_DO_STATUS[a.status] - PESO_DO_STATUS[b.status];
  }
}

function ordenar(linhas: readonly ConversionRow[], coluna: SortColumn, desc: boolean): readonly ConversionRow[] {
  return [...linhas].sort((a, b) => {
    const c = compararPor(a, b, coluna);
    return c !== 0 ? (desc ? -c : c) : ordemPadrao(a, b);
  });
}

function contarFacetas(linhas: readonly ConversionRow[], consulta: ConversionQuery, termo: string): ConversionFacets {
  const channel: Record<ChannelFilter, number> = { todos: 0, ligacao_ia: 0, whatsapp: 0, email: 0 };
  const status: Record<StatusFilter, number> = { todos: 0, a_faturar: 0, faturada: 0, paga: 0, revertida: 0 };
  const closedBy: Record<ClosedByFilter, number> = { todos: 0, motor: 0, comercial: 0, comercial_webhook: 0, venda_assistida: 0 };
  const refund: Record<RefundFilter, number> = { todos: 0, sem_estorno: 0, com_estorno: 0, mantida: 0, revertida: 0 };
  const amount: Record<AmountBand, number> = { todas: 0, ate_100: 0, de_100_a_500: 0, de_500_a_1000: 0, acima_1000: 0 };
  const passa = (l: ConversionRow, dim: DimensaoDoFiltro): boolean => passaNosFiltros(l, consulta, termo, dim);
  for (const l of linhas) {
    const o = opcoesDaLinha(l);
    if (passa(l, 'channel')) {
      channel.todos += 1;
      channel[o.channel] += 1;
    }
    if (passa(l, 'status')) {
      status.todos += 1;
      status[o.status] += 1;
    }
    if (passa(l, 'closedBy')) {
      closedBy.todos += 1;
      for (const opcao of o.closedBy) {
        closedBy[opcao] += 1;
      }
    }
    if (passa(l, 'refund')) {
      refund.todos += 1;
      for (const opcao of o.refund) {
        refund[opcao] += 1;
      }
    }
    if (passa(l, 'amount')) {
      amount.todas += 1;
      amount[o.amount] += 1;
    }
  }
  return { channel, status, closedBy, refund, amount };
}

function filtrar(range: PeriodRange, consulta: ConversionQuery): { readonly doRecorte: readonly ConversionRow[]; readonly filtradas: readonly ConversionRow[]; readonly termo: string } {
  const janela = janelaDe(range);
  const linhas = doRecorte(linhasDoIntervalo(janela.inicioMs, janela.fimMs), consulta.companyId);
  const termo = normalizar(consulta.search);
  return { doRecorte: linhas, filtradas: linhas.filter((l) => passaNosFiltros(l, consulta, termo)), termo };
}

export function simularExtrato(range: PeriodRange, consulta: ConversionQuery): ConversionsResponse {
  const { doRecorte: linhas, filtradas, termo } = filtrar(range, consulta);
  const ordenadas = ordenar(filtradas, consulta.sort, consulta.desc);
  const page: Page<ConversionRow> = {
    items: ordenadas.slice(consulta.offset, consulta.offset + consulta.limit),
    total: ordenadas.length,
    limit: consulta.limit,
    offset: consulta.offset,
  };
  return {
    page,
    facets: contarFacetas(linhas, consulta, termo),
    totals: {
      count: filtradas.length,
      paidAmountCents: filtradas.reduce((acc, l) => acc + l.paidAmountCents, 0),
      activeCommissionCents: filtradas.filter(ativa).reduce((acc, l) => acc + l.commissionCents, 0),
      reversedCommissionCents: filtradas.filter((l) => !ativa(l)).reduce((acc, l) => acc + l.commissionCents, 0),
    },
  };
}

export function simularExportacao(range: PeriodRange, consulta: ConversionQuery): ConversionsExport {
  const { filtradas } = filtrar(range, consulta);
  const ordenadas = ordenar(filtradas, consulta.sort, consulta.desc);
  return { rows: ordenadas.slice(0, LIMITE_DA_EXPORTACAO), total: ordenadas.length, truncated: ordenadas.length > LIMITE_DA_EXPORTACAO };
}
